import type React from 'react';
import {useCallback} from 'react';

import {useAnnotationModeStore} from './AnnotationModeStore';
import type {AnnotationAnchor} from './annotationTypes';
import {activeSelectionRange, isPortaledEvent} from './annotationUtils';
import {useIsAnnotationModeAvailable} from './useIsAnnotationModeAvailable';
import {useRegionEdit} from './useRegionEdit';

/** How a surface turns its mouseup gestures into editor anchors. */
interface AnnotationGesturePolicy {
  /** The region whose marks this surface edits. */
  regionId: string;
  /** A gesture that belongs elsewhere (non-primary button, exempt control): bail untouched. */
  shouldYield?: (event: React.MouseEvent<HTMLElement>) => boolean;
  /** Turns the gesture itself into an anchor (text capture); consumes the selection when it lands. */
  capture?: (event: React.MouseEvent<HTMLElement>) => AnnotationAnchor | null;
  /** The mark to edit at a plain click; null closes the editor (empty-space click). */
  resolveAtPoint: (event: React.MouseEvent<HTMLElement>) => AnnotationAnchor | null;
  /** Stop the acted-on click from also reaching a host region's handler. */
  consume?: boolean;
}

/**
 * Owns which of a surface's marks is being edited, and the one mouseup grammar
 * every surface shares: mode on → not someone else's gesture → a capture beats
 * a plain click → a live selection is never hijacked → act. The surface renders
 * the editor from `activeAnnotation`; `openEditor(null)` closes, so a hit-test
 * miss needs no branching.
 */
export function useAnnotationGesture({
  regionId,
  shouldYield,
  capture,
  resolveAtPoint,
  consume = false
}: AnnotationGesturePolicy): {
  activeAnnotation: AnnotationAnchor | null;
  /** The mode switched on AND still usable. Surfaces key their affordances off this. */
  canEdit: boolean;
  handleMouseUp: (event: React.MouseEvent<HTMLElement>) => void;
  openEditor: (annotation: AnnotationAnchor | null) => void;
  closeEditor: () => void;
} {
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  // Submitting turns neither the mode nor its chrome off, and the toggle that would
  // is already unmounted, so the gate is re-checked rather than trusted.
  const canEdit = useIsAnnotationModeAvailable() && isAnnotationModeOn;
  const [activeAnnotation, openEdit, closeEditor] = useRegionEdit<AnnotationAnchor>();
  // Null is a hit-test miss, so a surface can hand a resolved mark straight over.
  const openEditor = useCallback(
    (annotation: AnnotationAnchor | null) =>
      annotation ? openEdit(annotation, regionId) : closeEditor(),
    [openEdit, closeEditor, regionId]
  );

  // Rebuilt per render: policy closures carry render-scoped state by design.
  const handleMouseUp = (event: React.MouseEvent<HTMLElement>) => {
    if (!canEdit || isPortaledEvent(event) || shouldYield?.(event)) {
      return;
    }
    // Before the selection guard: capture resolves the selection into a mark.
    const captured = capture?.(event);
    if (captured) {
      openEditor(captured);
      return;
    }
    // An uncaptured live selection is never hijacked into a plain click
    // (select-to-copy in a region; the host region's drag over a supplement).
    if (activeSelectionRange()) {
      return;
    }
    if (consume) {
      event.stopPropagation();
    }
    openEditor(resolveAtPoint(event));
  };

  return {activeAnnotation, canEdit, handleMouseUp, openEditor, closeEditor};
}
